// Enhanced FilesPage.js - Adds Search While Preserving Existing Functionality
// September 24, 2025 - Keep all working code, add search features
import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, getDocs, orderBy } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import FileItem from '../components/ui/FileItem';
import './FilesPage.css';
// 26.11.25 AEST - Import filename cleaner utility
import { cleanFilename } from '../utils/filenameUtils';

const FilesPage = () => {
  // Keep all your existing state
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date');
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  
  // Add new search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  
  // Your existing fetchFiles function - UNCHANGED
  useEffect(() => {
    const fetchFiles = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      
      try {
        const db = getFirestore();
        const userEmail = currentUser.email;
        
        let filesQuery;
        
        // Different sorting options
        switch (sortBy) {
          case 'name':
            filesQuery = query(
              collection(db, 'Users', userEmail, 'Files'),
              orderBy('fileName', 'asc')
            );
            break;
            case 'category':
              // September 24, 2025 - Load all files, sort by category in JavaScript
              // (avoids Firestore errors when some files lack category field)
              filesQuery = query(
                collection(db, 'Users', userEmail, 'Files'),
                orderBy('uploadedAt', 'desc')
              );
              break;
          case 'date':
          default:
            filesQuery = query(
              collection(db, 'Users', userEmail, 'Files'),
              orderBy('uploadedAt', 'desc')
            );
            break;
        }
        
        const filesSnapshot = await getDocs(filesQuery);
        const filesList = filesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // September 24, 2025 - Handle category sorting with document_types.json order
        let sortedFiles = filesList;
        if (sortBy === 'category') {
          // Define category priority based on document_types.json
          const categoryPriority = {
            'NDIS': 1,
            'Support Plans': 2, 
            'Health': 3,
            'Service Agreements': 4,
            'Financial': 5,
            'Reports': 6,
            'Identification': 7,
            'Uncategorized': 99
          };
          
          sortedFiles = filesList.sort((a, b) => {
            const categoryA = a.category || 'Uncategorized';
            const categoryB = b.category || 'Uncategorized';
            
            const priorityA = categoryPriority[categoryA] || 50;
            const priorityB = categoryPriority[categoryB] || 50;
            
            if (priorityA !== priorityB) {
              return priorityA - priorityB;
            }
            
            // Secondary sort by date within category
            const dateA = a.uploadedAt?.toDate?.() || new Date(0);
            const dateB = b.uploadedAt?.toDate?.() || new Date(0);
            return dateB - dateA;
          });
        }

        setFiles(sortedFiles);
      } catch (err) {
        console.error('Error fetching files:', err);
        setError('Failed to load files. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFiles();
  }, [currentUser, sortBy]);
  
// Add search filtering when files or search term changes
  // 06.11.25 11:00am - Enhanced to search ALL metadata fields including OCR text, dates, organizations
  useEffect(() => {
    if (!searchTerm) {
      setFilteredFiles(files);
      return;
    }
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = files.filter(file => {
      try {
      // Search filename
      if (file.fileName?.toLowerCase().includes(searchLower)) return true;
      
      // Search category
      if (file.category?.toLowerCase().includes(searchLower)) return true;
      if (file.subCategory?.toLowerCase().includes(searchLower)) return true;
      
// 20.02.26 - Added String() conversion to handle non-string values in arrays
if (file.extractedKeywords?.some(keyword => 
  String(keyword).toLowerCase().includes(searchLower))) return true;
      
      // Search goals (for NDIS documents)
      if (file.extractedGoals?.some(goal => 
        goal.description?.toLowerCase().includes(searchLower) ||
        goal.title?.toLowerCase().includes(searchLower))) return true;
      
      // 06.11.25 11:00am - NEW: Search OCR extracted text from images/PDFs
      if (file.extractedText?.toLowerCase().includes(searchLower)) return true;
      
      // 06.11.25 11:00am - NEW: Search extracted dates
      if (file.allExtractedDates?.some(date => 
        String(date).includes(searchLower))) return true;
      
      // 06.11.25 11:00am - NEW: Search detected organizations
      if (file.detectedOrganizations?.some(org => 
        String(org).toLowerCase().includes(searchLower))) return true;
      
      // 06.11.25 11:00am - NEW: Search keywords array (if stored differently than extractedKeywords)
      if (file.keywords?.some(keyword => 
        String(keyword).toLowerCase().includes(searchLower))) return true;
      
        return false;
      } catch (err) {
        // 20.02.26 - If a file record has bad data, skip it rather than crash the page
        console.warn('Search filter error on file:', file.id, err);
        return false;
      }
    });
    
    setFilteredFiles(filtered);
  }, [files, searchTerm]);
  
  // Your existing sort handler - UNCHANGED
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };
  
  // Your existing file click handler - UNCHANGED (but moved to component level)
  const handleFileClick = async (file) => {
    console.log('File clicked:', file);
    
    try {
      // Generate fresh authenticated download URL
      const { getStorage, ref, getDownloadURL } = await import('firebase/storage');
      
      const storage = getStorage();
      const fileRef = ref(storage, file.path);
      
      const authenticatedURL = await getDownloadURL(fileRef);
      
      console.log('Opening with fresh authenticated URL');
      window.open(authenticatedURL, '_blank');
      
    } catch (error) {
      console.error('Error accessing file:', error);
      alert('Unable to open file - access denied');
    }
  };
  
  const filesToDisplay = searchTerm ? filteredFiles : files;
  
  return (
    <div className="files-page">
      <div className="files-header">
        <h1>My Files</h1>
        
        <div className="header-controls">
          {/* Add search toggle button */}
          <button 
            className="search-toggle-btn"
            onClick={() => setShowSearch(!showSearch)}
            title="Toggle search"
          >
            🔍 {showSearch ? 'Hide' : 'Search'}
          </button>
          
          {/* Keep your existing sort controls */}
          <div className="sort-controls">
            <span>Sort by:</span>
            <div className="sort-buttons">
              <button 
                className={sortBy === 'date' ? 'active' : ''}
                onClick={() => handleSortChange('date')}
              >
                Date
              </button>
              <button 
                className={sortBy === 'name' ? 'active' : ''}
                onClick={() => handleSortChange('name')}
              >
                Name
              </button>
              <button 
                className={sortBy === 'category' ? 'active' : ''}
                onClick={() => handleSortChange('category')}
              >
                Category
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add search input when toggled */}
      {showSearch && (
        <div className="search-container">
          <div className="search-input-wrapper">
          <input
              type="text"
              placeholder="Search files, categories, goals, keywords, dates, organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="clear-search"
              >
                ✕
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="search-results-info">
              Showing {filesToDisplay.length} of {files.length} files
              {filesToDisplay.length !== files.length && (
                <span className="search-match"> matching "{searchTerm}"</span>
              )}
            </div>
          )}
        </div>
      )}
      
      <div className="files-container">
        {loading ? (
          <div className="loading-spinner">Loading files...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filesToDisplay.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📁</div>
            <h2>{searchTerm ? 'No files found' : 'No files yet'}</h2>
            <p>
              {searchTerm ? 
                'Try different search terms or clear the search' : 
                'Upload your first document to get started'
              }
            </p>
            {!searchTerm && (
              <button className="upload-button" onClick={() => window.location.href = '/upload'}>
                Upload Document
              </button>
            )}
          </div>
        ) : (
          <div className="files-list">
            {filesToDisplay.map(file => (
              <div 
                key={file.id} 
                className="file-item-direct"
                onClick={() => handleFileClick(file)}
                style={{
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fff'
                }}
              >
<div>
                  {/* 26.11.25 AEST - Display clean filename without hash/timestamp */}
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {cleanFilename(file.fileName)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {file.category || 'Uncategorized'}
                    {/* Show search matches when searching - 06.11.25 11:15am Enhanced indicators */}
                    {searchTerm && file.extractedGoals && file.extractedGoals.length > 0 && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#128C7E' }}>
                        • {file.extractedGoals.length} goals
                      </span>
                    )}
                    {/* 06.11.25 11:15am - Show if match found in OCR text */}
                    {searchTerm && file.extractedText?.toLowerCase().includes(searchTerm.toLowerCase()) && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#0066cc' }}>
                        • OCR match
                      </span>
                    )}
                    {/* 06.11.25 11:15am - Show if match found in dates */}
                    {searchTerm && file.allExtractedDates?.some(d => String(d).includes(searchTerm.toLowerCase())) && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#ff6b6b' }}>
                        • Date match
                      </span>
                    )}
                    {/* 06.11.25 11:15am - Show if match found in organizations */}
                    {/* 20.02.26 - Added String() to handle non-string values in render */}
                    {searchTerm && file.detectedOrganizations?.some(o => String(o).toLowerCase().includes(searchTerm.toLowerCase())) && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#9c27b0' }}>
                        • Org match
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {file.uploadedAt?.toDate?.()?.toLocaleDateString?.() || 'No date'}
                </div>
              </div>
           ))}
           </div>
         )}
       </div>
     </div>
   );
 };
 
 export default FilesPage;